/**
 * Status-based encounter navigation.
 * Routes to the appropriate view based on encounter state:
 * - completed + has notes → Visit Summary
 * - in_progress + user has in-progress note → Clinical Note Edit
 * - has notes but none in-progress for user → Visit Summary
 * - no notes → Encounter Detail (legacy)
 */

/**
 * Get the destination path for an encounter based on its status.
 * @param {Object} encounter - The encounter object
 * @param {string} [userId] - Current user ID (to find their in-progress note)
 * @param {Object[]} [encounterNotes] - Pre-fetched notes for the encounter
 * @returns {string} - The path to navigate to
 */
export function getEncounterDestination(encounter, userId, encounterNotes) {
  if (encounterNotes && encounterNotes.length > 0) {
    if (encounter.status === 'completed') {
      return `/visits/${encounter.id}`;
    }
    if (userId) {
      const userNote = encounterNotes.find(
        n => (String(n.provider_id) === String(userId) || String(n.treating_provider_id) === String(userId) || String(n.assigned_signer_id) === String(userId)) && !n.signed
      );
      if (userNote) {
        return `/clinical-notes/${userNote.id}/edit`;
      }
    }
    return `/visits/${encounter.id}`;
  }

  // Legacy fallback: use clinical_note_id if no notes array provided
  if (encounter.clinical_note_id) {
    if (encounter.status === 'completed') {
      return `/visits/${encounter.id}`;
    }
    return `/clinical-notes/${encounter.clinical_note_id}/edit`;
  }
  return `/encounters/${encounter.id}`;
}

/**
 * Resolve where to navigate for an encounter, with multi-note picker support.
 * Returns { type: 'navigate', path } for direct navigation,
 * or { type: 'pick', notes, encounter } when user has multiple unsigned notes.
 */
export async function resolveEncounterNavigation(encounterId, apiService, userId) {
  const [encounter, notesResult] = await Promise.all([
    apiService.getEncounter(encounterId),
    apiService.getClinicalNotes({ encounter_id: encounterId })
  ]);
  const notes = notesResult?.items || notesResult || [];

  if (userId && notes.length > 0 && encounter.status !== 'completed') {
    const userUnsignedNotes = notes.filter(
      n => (String(n.provider_id) === String(userId) || String(n.treating_provider_id) === String(userId) || String(n.assigned_signer_id) === String(userId)) && !n.signed
    );
    if (userUnsignedNotes.length > 1) {
      return { type: 'pick', notes: userUnsignedNotes, encounter };
    }
  }

  return { type: 'navigate', path: getEncounterDestination(encounter, userId, notes) };
}

/**
 * Async version — fetches the encounter and its notes, then navigates.
 * For callers that don't need picker support.
 * @param {Function} navigate - React Router navigate function
 * @param {string} encounterId - The encounter ID
 * @param {Object} apiService - The API service instance
 * @param {string} [userId] - Current user ID
 */
export async function navigateToEncounter(navigate, encounterId, apiService, userId) {
  try {
    const [encounter, notesResult] = await Promise.all([
      apiService.getEncounter(encounterId),
      apiService.getClinicalNotes({ encounter_id: encounterId })
    ]);
    const notes = notesResult?.items || notesResult || [];
    navigate(getEncounterDestination(encounter, userId, notes));
  } catch (err) {
    console.error('Error navigating to encounter:', err);
    navigate(`/encounters/${encounterId}`);
  }
}

/**
 * Find recent in-progress encounters for a patient that aren't linked to an appointment.
 * @param {Object} apiService
 * @param {string} patientId
 * @param {number|string|null} caseId
 * @returns {Promise<Object[]>}
 */
export async function findRecentUnlinkedEncounters(apiService, patientId, caseId = null) {
  try {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const filters = {
      patient_id: patientId,
      start_date: yesterday.toISOString(),
      end_date: now.toISOString(),
      status: 'in_progress',
      limit: 10,
    };
    if (caseId) filters.case_id = caseId;

    const result = await apiService.getEncounters(filters);
    const encounters = result?.items || result || [];

    return encounters.filter(e => !e.appointment_id);
  } catch (err) {
    console.error('Error finding recent unlinked encounters:', err);
    return [];
  }
}
